import { z } from "zod/v4";

// Enums
export const ContextTypeSchema = z.enum([
  "shopData",
  "userHistory",
  "analytics",
  "category",
  "custom",
]);

export const FilterOperatorSchema = z.enum([
  "eq",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "nin",
  "contains",
  "exists",
]);

// Core schemas
export const FilterPredicateSchema = z.object({
  path: z.string(),
  operator: FilterOperatorSchema,
  value: z.unknown(),
});

export const ContextFilterSchema = z.object({
  include: z.array(z.string()).optional(),
  exclude: z.array(z.string()).optional(),
  where: z.array(FilterPredicateSchema).optional(),
  types: z
    .object({
      keep: z.array(ContextTypeSchema).optional(),
      remove: z.array(ContextTypeSchema).optional(),
    })
    .optional(),
  inherit: z.boolean().optional(),
  merge: z.enum(["replace", "append", "deep"]).optional(),
});

export const ContextSourceConfigSchema = z.object({
  id: z.string(),
  type: ContextTypeSchema,
  enabled: z.boolean().optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  cache: z.object({
    ttl: z.number().optional(),
    key: z.string(),
  }),
});

export const ConditionalLoaderSchema = z.object({
  condition: FilterPredicateSchema,
  sources: z.array(ContextSourceConfigSchema),
});

export const ContextAutoLoadConfigSchema = z.object({
  shopData: z.boolean().optional(),
  userHistory: z.boolean().optional(),
  categoryContexts: z.array(z.string()).optional(),
  analytics: z.boolean().optional(),
  customSources: z.array(ContextSourceConfigSchema).optional(),
});

export const ContextLoadTriggersSchema = z.object({
  onStart: z.boolean().optional(),
});

export const ContextConfigSchema = z.object({
  autoLoad: z.record(z.string(), z.string().array()),
});

export const BaseLoaderContextSchema = z.object({
  shopId: z.string(),
  sessionId: z.string(),
  userId: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type BaseLoaderContext = z.infer<typeof BaseLoaderContextSchema>;

export const CategoryLoaderContextSchema = BaseLoaderContextSchema.extend({
  type: z.literal("category"),
  metadata: z.object({
    category: z.string(),
    file: z.string(),
  }),
});

export type CategoryLoaderContext = z.infer<typeof CategoryLoaderContextSchema>;

export const CategoryContextLoaderDataSchema = z.object({
  category: z.string().optional(),
  content: z.string().optional(),
  files: z.string().array().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type CategoryContextLoaderData = z.infer<
  typeof CategoryContextLoaderDataSchema
>;

export const ShopContextLoaderContextSchema = BaseLoaderContextSchema.extend({
  type: z.literal("shopData"),
  metadata: z.object({
    shopId: z.string(),
  }),
});

export type ShopContextLoaderContext = z.infer<
  typeof ShopContextLoaderContextSchema
>;

export const LoaderContextSchema = z.discriminatedUnion("type", [
  CategoryLoaderContextSchema,
  ShopContextLoaderContextSchema,
]);

export const CacheEntrySchema = z.object({
  data: z.unknown(),
  timestamp: z.number(),
  ttl: z.number(),
});

export const LoadContextStatsSchema = z.object({
  sources: z.array(z.string()),
  loadTime: z.number(),
  cacheHits: z.number(),
  cacheMisses: z.number(),
});
export type LoadContextStats = z.infer<typeof LoadContextStatsSchema>;

export const DynamicFilterConfigSchema = z.object({
  nodeId: z.string(),
  outputPath: z.string(),
  templateName: z.string().optional(),
});

export const FilterValidationErrorSchema = z.object({
  path: z.string(),
  error: z.string(),
  suggestion: z.string().optional(),
});

// Type exports from schemas
export type ContextType = z.infer<typeof ContextTypeSchema>;
export type FilterOperator = z.infer<typeof FilterOperatorSchema>;
export type FilterPredicate = z.infer<typeof FilterPredicateSchema>;
export type ContextFilter = z.infer<typeof ContextFilterSchema>;
export type ContextSourceConfig = z.infer<typeof ContextSourceConfigSchema>;
export type ConditionalLoader = z.infer<typeof ConditionalLoaderSchema>;
export type ContextAutoLoadConfig = z.infer<typeof ContextAutoLoadConfigSchema>;
export type ContextLoadTriggers = z.infer<typeof ContextLoadTriggersSchema>;
export type ContextConfig = z.infer<typeof ContextConfigSchema>;
export type LoaderContext = z.infer<typeof LoaderContextSchema>;
export type CacheEntry = z.infer<typeof CacheEntrySchema>;

export type DynamicFilterConfig = z.infer<typeof DynamicFilterConfigSchema>;
export type FilterValidationError = z.infer<typeof FilterValidationErrorSchema>;

// Specific loader result schemas
export const ShopDataSchema = z.object({
  organization: z
    .object({
      id: z.string().optional(),
      shopId: z.string().optional(),
      domain: z.string().optional(),
      email: z.string().optional(),
      planName: z.string().optional(),
      settings: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
  products: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        vendor: z.string().optional(),
        productType: z.string().optional(),
        tags: z.array(z.string()).optional(),
        price: z.number().optional(),
        compareAtPrice: z.number().optional(),
        inventory: z.number().optional(),
      })
    )
    .optional(),
  policies: z
    .object({
      refundPolicy: z.string().optional(),
      privacyPolicy: z.string().optional(),
      termsOfService: z.string().optional(),
      shippingPolicy: z.string().optional(),
    })
    .optional(),
  metadata: z
    .object({
      loadedAt: z.string(),
      shopId: z.string(),
    })
    .optional(),
});

export const UserHistoryDataSchema = z.object({
  sessions: z
    .array(
      z.object({
        id: z.string(),
        timestamp: z.string(),
        actions: z.array(z.unknown()),
      })
    )
    .optional(),
  preferences: z.record(z.string(), z.unknown()).optional(),
  metadata: z
    .object({
      loadedAt: z.string(),
      userId: z.string(),
    })
    .optional(),
});

export const AnalyticsDataSchema = z.object({
  metrics: z.record(z.string(), z.number()).optional(),
  segments: z.array(z.string()).optional(),
  timeRange: z
    .object({
      start: z.string(),
      end: z.string(),
    })
    .optional(),
  metadata: z
    .object({
      loadedAt: z.string(),
      shopId: z.string(),
    })
    .optional(),
});

// Type exports for loader results
export type ShopData = z.infer<typeof ShopDataSchema>;
export type UserHistoryData = z.infer<typeof UserHistoryDataSchema>;
export type AnalyticsData = z.infer<typeof AnalyticsDataSchema>;

// Type guards for runtime validation
export const isShopData = (data: unknown): data is ShopData => {
  return ShopDataSchema.safeParse(data).success;
};

export const isUserHistoryData = (data: unknown): data is UserHistoryData => {
  return UserHistoryDataSchema.safeParse(data).success;
};

export const isAnalyticsData = (data: unknown): data is AnalyticsData => {
  return AnalyticsDataSchema.safeParse(data).success;
};

export const isCategoryContextLoaderData = (
  data: unknown
): data is CategoryContextLoaderData => {
  return CategoryContextLoaderDataSchema.safeParse(data).success;
};

export const isContextFilter = (data: unknown): data is ContextFilter => {
  return ContextFilterSchema.safeParse(data).success;
};

export const isFilterPredicate = (data: unknown): data is FilterPredicate => {
  return FilterPredicateSchema.safeParse(data).success;
};
